import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from "motion/react";
import { DocumentPage } from "../../components/layout/DocumentPage";
import { Reveal } from "../../components/motion/Reveal";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import styles from "./Cover.module.css";

export function Cover() {
  const reduceMotion = useReducedMotion();
  const [showScrollHint, setShowScrollHint] = useState(true);
  const showreelRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: showreelRef,
    offset: ["start end", "end start"],
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 115,
    damping: 28,
    mass: 0.35,
  });
  const showreelX = useTransform(smoothProgress, [0, 1], [20, -52]);
  const showreelScale = useTransform(smoothProgress, [0, 1], [0.98, 1.07]);
  const panelOneY = useTransform(smoothProgress, [0, 1], [26, -34]);
  const panelTwoY = useTransform(smoothProgress, [0, 1], [58, -82]);
  const panelThreeY = useTransform(smoothProgress, [0, 1], [92, -132]);

  useEffect(() => {
    const updateScrollHint = () => {
      const shouldShow = window.scrollY <= 8;
      setShowScrollHint((current) => current === shouldShow ? current : shouldShow);
    };

    updateScrollHint();
    window.addEventListener("scroll", updateScrollHint, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollHint);
  }, []);

  const scrollHint = (
    <AnimatePresence>
      {showScrollHint && (
        <motion.div
          className={`${styles.scrollHint} no-print`}
          aria-hidden="true"
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={reduceMotion
            ? { opacity: 1 }
            : { opacity: 1, y: [0, -5, 0], scale: [1, 1.018, 1] }}
          exit={reduceMotion
            ? { opacity: 0, transition: { duration: 0 } }
            : {
                opacity: 0,
                y: 8,
                transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
              }}
          transition={reduceMotion
            ? { duration: 0 }
            : {
                opacity: { duration: 0.4 },
                y: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
                scale: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
              }}
        >
          <span>↑</span>
          <strong className={styles.desktopHintText}>SCROLL &amp; SWIPE</strong>
          <strong className={styles.mobileHintText}>SWIPE</strong>
          <span>↓</span>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <DocumentPage id="cover" className={styles.coverPage}>
      <motion.div
        ref={showreelRef}
        className={styles.showreel}
        aria-hidden="true"
        initial={reduceMotion ? false : { opacity: 0, y: 180, rotate: 8 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        style={reduceMotion ? undefined : { x: showreelX, scale: showreelScale }}
        transition={{ duration: 1.05, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.span style={reduceMotion ? undefined : { y: panelOneY }} />
        <motion.span style={reduceMotion ? undefined : { y: panelTwoY }} />
        <motion.span style={reduceMotion ? undefined : { y: panelThreeY }} />
      </motion.div>
      <div className={styles.hero}>
        <Reveal delay={0.15} distance={24} className={styles.name}>
          <p>심현철 <span>SIM HYEON CHEOL</span></p>
        </Reveal>
        <Reveal delay={0.38} distance={38}>
          <h1>FULL-STACK<br />DEVELOPER</h1>
          <p className={styles.tagline}>
            WEB · Backend · API · DB부터
            <br />
            모바일 앱 · SDK · 외부 장비 연동까지,
            <br />
            운영 가능한 서비스 구조로 구현합니다.
          </p>
        </Reveal>
      </div>
      <div className={styles.summary}>
        <Reveal delay={0.7} distance={20} className={styles.identity}>
          <span>직무</span>
          <strong>Full-Stack Developer</strong>
        </Reveal>
        <Reveal delay={0.82} distance={20} className={styles.skills}>
          <span>주요 역량</span>
           <p>
            Web · Backend · Android · iOS
            <br />
            SDK · BLE · RDBMS · GitHub Actions · CI/CD
          </p>
        </Reveal>
        <Reveal delay={0.94} distance={20} className={styles.year}>
          <span>Portfolio</span>
          <strong>2026</strong>
        </Reveal>
      </div>
      </DocumentPage>
      {createPortal(scrollHint, document.body)}
    </>
  );
}
